import React from "react";
import { Box, Typography } from "@mui/material";

/**
 * AHA modules taught in one session, chosen from the class's AHA product. Modules in the same exclusive group are
 * alternatives (e.g. Heartsaver Total vs Basic): choosing one clears the others, so a session can never carry a
 * combination AHA would reject. Each student's eCard carries the modules of the sessions they attended.
 *
 * @param {{ modules: Array<{key: string, name: string, exclusiveGroup?: string}>, value: string[],
 *           onChange: (keys: string[]) => void, locked?: boolean, labelSx?: object }} props
 */
export default function AhaSessionModulesField({ modules = [], value = [], onChange, locked = false, labelSx }) {
  if (!modules.length) return null;

  const toggle = (module, checked) => {
    let next = value.filter((key) => key !== module.key);
    if (checked) {
      if (module.exclusiveGroup) {
        const siblings = modules.filter((m) => m.exclusiveGroup === module.exclusiveGroup).map((m) => m.key);
        next = next.filter((key) => !siblings.includes(key));
      }
      next = [...next, module.key];
    }
    onChange(next);
  };

  return (
    <Box role="group" aria-labelledby="aha-session-modules-label">
      <Typography id="aha-session-modules-label" sx={labelSx || { fontSize: 12, fontWeight: 700, mb: "6px" }}>
        AHA modules taught in this session
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
        {modules.map((module) => {
          const chosen = value.includes(module.key);
          return (
            <Box key={module.key} component="label" sx={{
              display: "inline-flex", alignItems: "center", gap: "6px", px: "10px", height: 30,
              borderRadius: "999px", cursor: locked ? "not-allowed" : "pointer", fontSize: 12.5,
              opacity: locked ? 0.6 : 1,
              border: `1.5px solid ${chosen ? "var(--color-primary)" : "var(--color-hairline-cloud)"}`,
              bgcolor: chosen ? "color-mix(in srgb, var(--color-primary) 8%, transparent)" : "var(--color-surface-press-light)",
              color: "var(--color-ink)",
            }}>
              <Box component="input" type="checkbox" checked={chosen} disabled={locked}
                onChange={(e) => toggle(module, e.target.checked)} sx={{ m: 0, cursor: "inherit" }} />
              {module.name}{module.exclusiveGroup ? " (one of the alternatives)" : ""}
            </Box>
          );
        })}
      </Box>
      <Typography sx={{ fontSize: 11.5, color: "var(--color-accent-violet-mid)", mt: "5px" }}>
        {locked
          ? "AHA eCards have already been requested for this class, so its session modules can no longer change."
          : "Each student's eCard carries the modules from the sessions they attended."}
      </Typography>
    </Box>
  );
}
